import { DOCUMENT } from '@angular/common';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter, interval } from 'rxjs';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class PwaUpdateService {
  readonly #swUpdate = inject(SwUpdate);
  readonly #destroyRef = inject(DestroyRef);
  readonly #document = inject(DOCUMENT);

  readonly updateAvailable = signal(false);
  readonly isChecking = signal(false);

  readonly isEnabled = this.#swUpdate.isEnabled;

  initialize(): void {
    if (!this.#swUpdate.isEnabled) return;

    this.#listenForVersionReady();
    this.#startPeriodicCheck();
  }

  #listenForVersionReady(): void {
    this.#swUpdate.versionUpdates
      .pipe(
        filter(
          (event): event is VersionReadyEvent => event.type === 'VERSION_READY',
        ),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => {
        console.log('[PwaUpdateService] New version ready');
        this.updateAvailable.set(true);
      });
  }

  #startPeriodicCheck(): void {
    interval(CHECK_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.checkForUpdate());
  }

  async checkForUpdate(): Promise<boolean> {
    if (!this.#swUpdate.isEnabled) return false;

    this.isChecking.set(true);
    try {
      const hasUpdate = await this.#swUpdate.checkForUpdate();
      console.log('[PwaUpdateService] Check for update:', hasUpdate);
      return hasUpdate;
    } catch (error) {
      console.error('[PwaUpdateService] Error checking for update:', error);
      return false;
    } finally {
      this.isChecking.set(false);
    }
  }

  async activateUpdate(): Promise<void> {
    if (!this.#swUpdate.isEnabled) return;

    try {
      await this.#swUpdate.activateUpdate();
      this.#document.location.reload();
    } catch (error) {
      console.error('[PwaUpdateService] Error activating update:', error);
      this.#document.location.reload();
    }
  }
}
